/** Mandi purchase settlement options (mirror backend/parties/mandi_settlement.py). */

export const WEIGHT_BASIS_OPTIONS = [
  { value: "FIXED_PER_BAG", label: "Fixed per bag (40/60 kg)" },
  { value: "WEIGHBRIDGE_MINUS_BARDANA", label: "Party weighbridge − bardana" },
];

export const FREIGHT_ALLOWANCE_OPTIONS = [
  { value: "GM_100_PER_BAG", label: "100 gm per bag" },
  { value: "GM_200_PER_BAG", label: "200 gm per bag" },
  { value: "GM_500_PER_BAG", label: "500 gm per bag" },
  { value: "KG_50_PER_TRUCK", label: "50 kg per truck" },
  { value: "KG_100_PER_TRUCK", label: "100 kg per truck" },
  { value: "KG_150_PER_TRUCK", label: "150 kg per truck" },
];

const FIXED_BAG_KG = { JUTE_BAG_40KG: 40, JUTE_BAG_60KG: 60, BAG_40KG: 40, BAG_60KG: 60 };

export const DEFAULT_BARDANA_RULES = {
  JUTE_BAG_40KG: { material_kg_per_bag: 40, kg_per_bag: 1 },
  JUTE_BAG_60KG: { material_kg_per_bag: 60, kg_per_bag: 1 },
};

/** Mandi bag-weight presets for supplier party form. */
export const BAG_WEIGHT_PRESETS = {
  JUTE_BAG_60KG: [
    {
      id: "plus_bardana",
      label: "60 kg grain + 1 kg bardana (61 kg total)",
      material_kg_per_bag: 60,
      kg_per_bag: 1,
    },
    {
      id: "includes_bardana",
      label: "60 kg bag incl. bardana (59 + 1)",
      material_kg_per_bag: 59,
      kg_per_bag: 1,
    },
    {
      id: "grain_54",
      label: "54 kg grain + 1 kg bardana (55 kg total)",
      material_kg_per_bag: 54,
      kg_per_bag: 1,
    },
  ],
  JUTE_BAG_40KG: [
    {
      id: "plus_bardana",
      label: "40 kg grain + 1 kg bardana (41 kg total)",
      material_kg_per_bag: 40,
      kg_per_bag: 1,
    },
    {
      id: "includes_bardana",
      label: "40 kg bag incl. bardana (39 + 1)",
      material_kg_per_bag: 39,
      kg_per_bag: 1,
    },
  ],
};

function bardanaRuleRow(bardanaRules, bardanaCode) {
  if (!bardanaRules || !bardanaCode) return null;
  const row = bardanaRules[bardanaCode];
  return row && typeof row === "object" ? row : null;
}

export function partyBardanaKgPerBag(bardanaRules, bardanaCode) {
  const row = bardanaRuleRow(bardanaRules, bardanaCode);
  if (row?.kg_per_bag != null) return Math.max(0, Number(row.kg_per_bag) || 0);
  return 1;
}

export function partyMaterialKgPerBag(bardanaRules, bardanaCode) {
  const nominal = FIXED_BAG_KG[bardanaCode] || 0;
  const row = bardanaRuleRow(bardanaRules, bardanaCode);
  if (row?.material_kg_per_bag != null) return Math.max(0, Number(row.material_kg_per_bag) || 0);
  return nominal;
}

/** Example threshold for fixed-per-bag mandi settlement. */
export function mandiThresholdHint(bags, bagCode, bardanaRules = null) {
  const n = Number(bags) || 0;
  const materialKg = partyMaterialKgPerBag(bardanaRules, bagCode);
  const bardanaKg = partyBardanaKgPerBag(bardanaRules, bagCode);
  if (!n || !materialKg) return null;
  const itemQtl = (n * materialKg) / 100;
  const bardanaQtl = Math.round(n * bardanaKg) / 100;
  return {
    itemQtl,
    bardanaQtl,
    materialKgPerBag: materialKg,
    bardanaKgPerBag: bardanaKg,
    grossKgPerBag: materialKg + bardanaKg,
    thresholdQtl: Math.round((itemQtl + bardanaQtl) * 100) / 100,
  };
}

/** Mirror backend compute_mandi_billable_qtl for gate reservation preview. */
export function computeMandiBillableQtl({
  weightBasis = "WEIGHBRIDGE_MINUS_BARDANA",
  bardanaCode,
  bags = 0,
  partyNetQtl = 0,
  ourNetQtl = 0,
  bardanaKgPerBag,
  materialKgPerBag,
  partyBardanaRules = null,
  deliveryCondition = "TRUCK_LOAD",
}) {
  const n = Math.max(0, Number(bags) || 0);
  const partyNet = Math.round(Number(partyNetQtl || 0) * 100) / 100;
  const delivery = String(deliveryCondition || "TRUCK_LOAD").toUpperCase();
  const materialNet = delivery === "TRUCK_LOAD" && partyNet > 0 ? partyNet : Math.round(Number(ourNetQtl || 0) * 100) / 100;
  const kgPerBag = bardanaKgPerBag != null
    ? Math.max(0, Number(bardanaKgPerBag) || 0)
    : partyBardanaKgPerBag(partyBardanaRules, bardanaCode);
  const materialKg = materialKgPerBag != null
    ? Math.max(0, Number(materialKgPerBag) || 0)
    : partyMaterialKgPerBag(partyBardanaRules, bardanaCode);
  const bardanaQtl = Math.round(n * kgPerBag) / 100;
  const basis = String(weightBasis || "WEIGHBRIDGE_MINUS_BARDANA").toUpperCase();
  const itemQtl = materialKg && n ? Math.round((n * materialKg) / 100 * 100) / 100 : 0;
  const thresholdQtl = Math.round((itemQtl + bardanaQtl) * 100) / 100;
  let billableQtl;
  let weightMode;
  if (basis === "FIXED_PER_BAG" && materialKg && n) {
    if (materialNet >= thresholdQtl) {
      billableQtl = itemQtl;
      weightMode = "fixed_per_bag";
    } else {
      billableQtl = Math.round(Math.max(0, materialNet - bardanaQtl) * 100) / 100;
      weightMode = "party_bridge_minus_bardana";
    }
  } else {
    billableQtl = Math.round(Math.max(0, materialNet - bardanaQtl) * 100) / 100;
    weightMode = "weighbridge_minus_bardana";
  }
  return {
    billableQtl,
    bardanaQtl,
    itemQtl,
    thresholdQtl,
    weightMode,
    partyNetQtl: partyNet,
    bags: n,
    bardanaKgPerBag: kgPerBag,
    materialKgPerBag: materialKg,
    grossKgPerBag: materialKg + kgPerBag,
  };
}

/** Billable qtl reserved on contract from party net + gate bags (truck load). */
export function reservationBillableForContract(contract, partyNetQtl, bags, partyBardanaRules = null) {
  const partyNet = Math.round(Number(partyNetQtl || 0) * 100) / 100;
  const bagCount = Math.max(0, Number(bags) || 0);
  if (!contract || String(contract.delivery_condition || "").toUpperCase() !== "TRUCK_LOAD") {
    return { billableQtl: partyNet, reservedQtl: partyNet, partyNetQtl: partyNet, bags: bagCount };
  }
  if (contract.purchase_type === "MANDI_PURCHASE") {
    const detail = computeMandiBillableQtl({
      weightBasis: contract.weight_basis,
      bardanaCode: contract.bardana,
      bags: bagCount,
      partyNetQtl: partyNet,
      deliveryCondition: contract.delivery_condition,
      partyBardanaRules,
    });
    return { ...detail, reservedQtl: detail.billableQtl };
  }
  return { billableQtl: partyNet, reservedQtl: partyNet, partyNetQtl: partyNet, bags: bagCount };
}
